import { memo, useMemo } from 'react';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

// project imports
import MenuCard from './MenuCard';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';
import { useNavTheme, NavThemeTokens } from 'contexts/NavThemeContext';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { drawerWidth, drawerWidthCollapsed, HEADER_HEIGHT } from 'store/constant';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| SIDEBAR BACKGROUND ||============================== //

interface SidebarBackgroundProps {
  isCollapsed?: boolean;
  tokens: NavThemeTokens;
  mode: 'dark' | 'light';
}

function SidebarBackground({ tokens, mode }: SidebarBackgroundProps) {
  const isLight = mode === 'light';

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        // Clean solid background - matches integrations/analytics pages
        background: isLight ? '#ffffff' : tokens.bgPrimary,
        transition: 'background 0.3s ease',
      }}
    />
  );
}

// ==============================|| SIDEBAR DRAWER ||============================== //

function Sidebar() {
  const downMD = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));  // < 900px (mobile)
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));  // < 1200px (tablet)
  const { mode, tokens } = useNavTheme();

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const {
    state: { menuOrientation }
  } = useConfig();

  const isLight = mode === 'light';

  // Determine if sidebar should be visible (not mobile)
  const showSidebar = !downMD;

  // On tablet and desktop, respect drawerOpen state (hamburger toggle works at all sizes)
  const effectiveDrawerOpen = downMD ? false : drawerOpen;

  const logo = useMemo(
    () => (
      <Box
        sx={{
          display: drawerOpen ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 2.5,
          py: 2.5,
          mb: 1,
          position: 'relative',
          zIndex: 1,
          // Clean bottom border - matches integrations/analytics
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '10%',
            right: '10%',
            height: '1px',
            background: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
            transition: 'background 0.3s ease',
          }
        }}
      >
        <LogoSection />
      </Box>
    ),
    [drawerOpen, isLight]
  );

  const drawer = useMemo(() => {
    const isDrawerExpanded = menuOrientation === MenuOrientation.VERTICAL && effectiveDrawerOpen;

    return (
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          height: '100%',
          overflow: 'auto',
          pt: effectiveDrawerOpen ? 0 : 1,
          // Clean scrollbar styling
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            '&:hover': {
              background: isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.15)',
            },
          }
        }}
      >
        <MenuList />
        {/* Show full MenuCard when expanded, collapsed version when minimized */}
        {isDrawerExpanded ? (
          <MenuCard />
        ) : (
          <MenuCard isCollapsed />
        )}
      </Box>
    );
  }, [effectiveDrawerOpen, menuOrientation, isLight]);

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { md: 0 },
        // Mobile: auto width (no sidebar)
        // Tablet: collapsed width (72px)
        // Desktop: full width or collapsed based on drawerOpen state
        width: {
          xs: 'auto',
          md: drawerOpen ? drawerWidth : drawerWidthCollapsed,  // Tablet+Desktop: respect drawer state
        },
        position: 'relative',
        transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      aria-label="mailbox folders"
    >
      {/* Right edge border line - clean, matches other pages */}
      {showSidebar && (
        <Box
          sx={{
            position: 'fixed',
            top: HEADER_HEIGHT,
            left: effectiveDrawerOpen ? drawerWidth - 1 : drawerWidthCollapsed - 1,
            bottom: 0,
            width: '1px',
            background: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
            zIndex: 1100,
            transition: 'left 0.4s cubic-bezier(0.16, 1, 0.3, 1), background 0.3s ease',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Mobile: temporary drawer overlay */}
      {downMD ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(false)}
          slotProps={{
            paper: {
              sx: {
                width: drawerWidth,
                background: isLight ? '#ffffff' : tokens.bgPrimary,
                color: tokens.textPrimary,
                borderRight: 'none',
              }
            }
          }}
          ModalProps={{ keepMounted: true }}
        >
          {logo}
          {drawer}
        </Drawer>
      ) : (
        /* Tablet/Desktop: permanent mini drawer with collapse support */
        <MiniDrawerStyled variant="permanent" open={effectiveDrawerOpen}>
          <SidebarBackground tokens={tokens} mode={mode} />
          {drawer}
        </MiniDrawerStyled>
      )}
    </Box>
  );
}

export default memo(Sidebar);
